import React, { isValidElement } from 'react';
import Input from './Input';
import getIn from './utils/getIn';
import { IDropdownOptionRenderer } from '../../@types/entityTypes';

type IReadOnlyComponentProps<T> = {
  value: T;
  'data-testid': string;
  testId?: string; //Deprecated - Please use 'data-testid'
  enum?: boolean;
  valueOnly?: boolean;
  options?: Array<any>;
  data?: Array<any>;
  valueKey: string;
  labelKey: string;
  valueRenderer?: IDropdownOptionRenderer;
};

/**
 * Get the (string) value to display in a dropdown component, taking into consideration
 * that if 'enum' or 'valueOnly' is set, we may need to obtain the human-friendly value from the options list.
 * (I.e. Convert 'YES' to 'Yes', or 5 to 'Fred the Customer')
 * @param propValue - The value or value object.
 * @param props - Other pass-through props from ReadOnlyDropdownComponent.
 */
const getReadOnlySingleValue = ({
  value: propValue,
  enum: isEnum,
  valueOnly,
  valueKey,
  options,
  labelKey,
  data,
}: IReadOnlyComponentProps<any>): string => {
  let value = getIn(propValue, labelKey);

  if (isEnum || valueOnly) {
    const optionList = options ? options : data;
    if (Array.isArray(optionList)) {
      optionList.some((option) => {
        if (option[valueKey] === propValue) {
          value = option[labelKey];
          return true;
        }
        return false;
      });
    }
  }

  if (value === undefined) {
    value = propValue;
  }

  return value;
};

/**
 * When a dropdown is set to read-only, this will be displayed instead of the regular view component.
 *  - If there is a valueRenderer provided, then use that to display the value.
 *  - Otherwise, get the value with the 'getReadOnlySingleValue' function and display it in a disabled Input (Legacy).
 * @param propValue - The selected value.
 * @param otherProps - Pass-through properties for consumption by getReadOnlySingleValue (Legacy)
 */
export const ReadOnlyComponent = ({ value: propValue, ...otherProps }: IReadOnlyComponentProps<Array<any> | any>) => {
  const ValueRenderer = otherProps.valueRenderer;
  if (ValueRenderer) {
    //If there's a valueRenderer, it now renders a component, so we need to handle it appropriately.
    if (Array.isArray(propValue)) {
      return (
        <>
          {propValue.map((value, idx) => {
            return (
              <>
                {idx > 0 ? ', ' : ''}
                <ValueRenderer
                  key={value?.id ?? value?.uuid ?? value}
                  data-testid={`${otherProps['data-testid']}_${idx}`}
                  option={value}
                />
              </>
            );
          })}
        </>
      );
    }
    return <ValueRenderer data-testid={otherProps['data-testid']} option={propValue} />;
  }
  let readOnlyValue: string;

  if (Array.isArray(propValue)) {
    readOnlyValue = propValue
      .map((value) => {
        return getReadOnlySingleValue({ value, ...otherProps });
      })
      .join(',');
  } else {
    readOnlyValue = getReadOnlySingleValue({ value: propValue, ...otherProps });
    if (isValidElement(readOnlyValue)) return readOnlyValue;
  }

  return <Input value={readOnlyValue} disabled data-testid={otherProps['data-testid'] || otherProps.testId} />;
};
